import { authHeaders } from "../lib/http";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useShopVertical } from "../lib/useShopVertical";
import {
  TrendingUp, Loader2, Calendar, Sparkles, ArrowUp, Star,
  Package, Eye, Heart, Tag, ChevronRight, AlertCircle,
  Zap, Target, BarChart3, Clock, Lightbulb, ShoppingBag,
  Flame, ArrowUpRight, MessageSquare, Filter, Store,
  ClipboardList, Plus, Check, Newspaper, Globe, ShoppingCart,
  X, FileText, Flag, Radar, EyeOff, RotateCw, Wand2, Search, Settings2,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { clsx } from "clsx";

// ========= Types =========

interface SeasonalEvent {
  id: string;
  name: string;
  /** YYYY-MM-DD */
  date: string;
  products: Array<"tshirt" | "ornament">;
  /** Listeleme icin kalan gun; negatifse POD icin gec */
  listByDays: number;
  category: string;
  keywords: string[];
  designIdeas: string[];
  priority: "high" | "medium" | "low";
  daysUntil: number;
}

interface CalendarData {
  events: SeasonalEvent[];
  currentSeason: string;
  seasonKeywords: string[];
  totalUpcoming: number;
}

interface RisingProduct {
  id: string;
  title: string;
  shopName: string;
  imageUrl: string | null;
  price: number | null;
  url: string;
  currentFavorites: number;
  currentReviews: number;
  favoriteDelta: number;
  reviewDelta: number;
  growthScore: number;
  tags: string[];
  category: string | null;
}

interface NewProduct {
  id: string;
  title: string;
  shopName: string;
  imageUrl: string | null;
  price: number | null;
  url: string;
  firstSeenAt: string;
  tags: string[];
  category: string | null;
}

interface ThemeData {
  popularThemes: Array<{ theme: string; productCount: number; avgFavorites: number; avgReviews: number; score: number }>;
  emergingThemes: Array<{ theme: string; count: number }>;
  categories: Array<{ name: string; count: number }>;
  totalProducts: number;
  totalShops: number;
}

interface AISuggestion {
  weeklyTrends?: Array<{
    theme: string;
    reason: string;
    keywords: string[];
    designIdeas: string[];
    targetAudience: string;
    priceRange: string;
    urgency: string;
  }>;
  monthlyForecast?: Array<{
    month: string;
    themes: string[];
    topOpportunity: string;
    designTip: string;
  }>;
  nicheOpportunities?: Array<{
    niche: string;
    description: string;
    exampleDesigns: string[];
    competitionLevel: string;
    estimatedDemand: string;
  }>;
  actionItems?: string[];
}

interface SummaryData {
  trackedShops: number;
  trackedKeywords: number;
  discoveredTrends: number;
  newProductsThisWeek: number;
}

interface GundemDesignIdea {
  idea: string;
  productTypes: string[];
  targetAudience: string;
  suggestedText: string;
}

interface TrendSourceItem {
  source: "google_trends" | "wikipedia" | "ticketmaster";
  url: string;
  label: string;
  news?: Array<{ title: string; url: string; source: string }>;
}

interface TrendEventRow {
  id: string;
  title: string;
  category: string;
  startsAt: string | null;
  sources: TrendSourceItem[];
  firstSeenAt: string;
}

const SOURCE_LABELS: Record<TrendSourceItem["source"], string> = {
  google_trends: "Google Trends",
  wikipedia: "Wikipedia",
  ticketmaster: "Ticketmaster",
};

function SourceLinks({ sources }: { sources: TrendSourceItem[] }) {
  return (
    <span className="flex flex-wrap gap-x-3 gap-y-1">
      {sources.map((s) => (
        <a key={s.source} href={s.url} target="_blank" rel="noreferrer" className="text-xs text-indigo-700 hover:underline" onClick={(e) => e.stopPropagation()}>
          {SOURCE_LABELS[s.source]} · {s.label}
        </a>
      ))}
    </span>
  );
}

interface GundemEvent {
  eventId?: string;
  sources?: TrendSourceItem[];
  event: string;
  category: string;
  description: string;
  whyItSells: string;
  designIdeas: GundemDesignIdea[];
  urgency: string;
  trendWindow: string;
}

interface GundemData {
  trendingEvents: GundemEvent[];
  quickWins: string[];
  summary: string;
}

type ReviewDateFilter = "all" | "this_week" | "this_month" | "last_month" | "last_3_months" | "last_6_months" | "custom";

interface TopReviewedListing {
  title: string;
  url: string | null;
  shopName: string;
  shopId: string;
  count: number;
  avgRating: number;
  imageUrl: string | null;
  monthly: Array<{ key: string; label: string; count: number }>;
  daily: Array<{ key: string; label: string; dayName: string; count: number }>;
}

interface TopReviewedData {
  listings: TopReviewedListing[];
  totalReviews: number;
  shopCount: number;
}

interface HotProduct {
  id: string;
  title: string;
  shopName: string;
  shopId: string;
  imageUrl: string | null;
  price: number | null;
  url: string;
  inCarts: number | null;
  recentPurchases: number | null;
  hotMetricsAt: string | null;
  favoriteCount: number;
  reviewCount: number;
}

interface HotProductsData {
  products: HotProduct[];
  shopCount: number;
}

// ========= API Helpers =========

function getBackendUrl() {
  return import.meta.env.VITE_API_URL || "http://localhost:3001";
}

// Aktif magaza (X-Shop-Id) dahil ortak basliklar
const getAuthHeaders = () => authHeaders();

type JewelryIdea = {
  name: string; date: string; daysUntil: number | null;
  angles: Array<{ title: string; note: string; matchCount: number; examples: Array<{ catalogId: string; categoryId: string; name: string }> }>;
};
async function fetchJewelryIdeas(): Promise<JewelryIdea[]> {
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/jewelry-ideas?days=120`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Takı fikirleri alınamadı");
  return (await res.json()).data;
}

async function fetchCalendar(): Promise<CalendarData> {
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/calendar?days=90`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Takvim alınamadı");
  return (await res.json()).data;
}

async function fetchRisingProducts(): Promise<{ rising: RisingProduct[]; newProducts: NewProduct[] }> {
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/rising-products?days=14&limit=30`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Yükselen ürünler alınamadı");
  return (await res.json()).data;
}

async function fetchThemes(): Promise<ThemeData> {
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/theme-discovery`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Tema analizi alınamadı");
  return (await res.json()).data;
}

async function fetchSummary(): Promise<SummaryData> {
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/summary`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Özet alınamadı");
  return (await res.json()).data;
}

async function fetchHotProducts(): Promise<HotProductsData> {
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/hot-products`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Hot ürünler alınamadı");
  return (await res.json()).data;
}

async function fetchTopReviewed(filter: ReviewDateFilter, customStart?: string, customEnd?: string): Promise<TopReviewedData> {
  const params = new URLSearchParams({ filter });
  if (filter === "custom" && customStart) params.set("customStart", customStart);
  if (filter === "custom" && customEnd) params.set("customEnd", customEnd);
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/top-reviewed?${params}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("En çok yorumlu ürünler alınamadı");
  return (await res.json()).data;
}

async function fetchGundem(params?: { categories?: string[]; productTypes?: string[] }): Promise<{ gundem: GundemData | null; rawContent?: string }> {
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/gundem`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(params || {}),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Gündem analizi başarısız");
  }
  return (await res.json()).data;
}

interface TrendIdeaRow {
  id: string;
  eventTitle: string;
  idea: string;
  suggestedText: string;
  productTypes: string[];
  targetAudience: string | null;
  keyword: string;
  urgency: string;
  trendWindow: string | null;
  demandScore: number | null;
  competitionScore: number | null;
  opportunityScore: number | null;
  totalResults: number | null;
  ipLevel: "high" | "warn" | "info" | null;
  ipHits: Array<{ phrase: string; level: string; marks: Array<{ wordmark: string; owner: string }> }> | null;
  score: number | null;
  status: "pending" | "checking" | "ready" | "failed";
  error: string | null;
  designStatus: "generating" | "done" | "failed" | null;
  designError: string | null;
  designs: Array<{ id: string; url: string; model: string | null }>;
  performance: { drafts: number; published: number; views: number; favorites: number; sales: number; listingIds: string[] } | null;
  sources: TrendSourceItem[];
  createdAt: string;
}

interface RadarSettings { autoDesign: boolean; threshold: number; models: string[] }

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${getBackendUrl()}${path}`, { ...init, headers: getAuthHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || "İşlem başarısız");
  return body.data ?? body;
}

function RadarSettingsPanel() {
  const settingsQ = useQuery({ queryKey: ["radar-settings"], queryFn: () => api<RadarSettings>("/api/trend-intelligence/radar-settings") });
  const modelsQ = useQuery({ queryKey: ["builder-models"], queryFn: () => apiClient.getBuilderModels() });
  const save = useMutation({
    mutationFn: (s: Partial<RadarSettings>) => api<RadarSettings>("/api/trend-intelligence/radar-settings", { method: "PUT", body: JSON.stringify(s) }),
    onSuccess: () => void settingsQ.refetch(),
  });
  const s = settingsQ.data;
  if (!s) return null;
  const imageModels = modelsQ.data?.imageGen ?? [];
  const toggleModel = (id: string) => {
    const next = s.models.includes(id) ? s.models.filter((m) => m !== id) : [...s.models, id].slice(-3);
    if (next.length) save.mutate({ models: next });
  };
  return (
    <details className="rounded-xl border border-gray-200 bg-white">
      <summary className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-700">
        <Settings2 className="h-4 w-4" /> Tasarım ayarları
        <span className="text-xs font-normal text-gray-500">
          {s.autoDesign ? `otomatik tasarım açık (puan ≥ ${s.threshold})` : "otomatik tasarım kapalı"} · {s.models.length} model
        </span>
      </summary>
      <div className="space-y-4 border-t border-gray-100 p-4">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={s.autoDesign} onChange={(e) => save.mutate({ autoDesign: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
          Puan eşiğini geçen fikirde tasarımı otomatik üret
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          Puan eşiği
          <input type="number" min={1} max={100} defaultValue={s.threshold}
            onBlur={(e) => { const n = Number(e.target.value); if (n >= 1 && n <= 100 && n !== s.threshold) save.mutate({ threshold: n }); }}
            className="w-20 rounded-lg border border-gray-200 px-2 py-1 text-sm" />
        </label>
        <div>
          <p className="text-xs font-medium text-gray-500">Tasarım modelleri (en fazla 3; seçilenlerin hepsi paralel çalışır, her biri ayrı ücretlenir)</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {imageModels.map((m) => (
              <button key={m.id} onClick={() => toggleModel(m.id)}
                className={clsx("rounded-full border px-2.5 py-1 text-xs font-medium",
                  s.models.includes(m.id) ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50")}>
                {m.name}
              </button>
            ))}
          </div>
        </div>
        {save.isError && <p className="text-sm text-red-600">{(save.error as Error).message}</p>}
      </div>
    </details>
  );
}

async function fetchTrendIdeas(): Promise<TrendIdeaRow[]> {
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/ideas`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Fikirler yüklenemedi");
  return (await res.json()).data;
}

async function patchTrendIdea(id: string, status: "dismissed" | "pending") {
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/ideas/${id}`, {
    method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error("Güncellenemedi");
}

const IP_BADGE: Record<string, { label: string; cls: string }> = {
  high: { label: "Marka riski yüksek", cls: "bg-red-50 text-red-600" },
  warn: { label: "Marka uyarısı", cls: "bg-amber-50 text-amber-700" },
  info: { label: "Marka notu", cls: "bg-gray-100 text-gray-600" },
};

function RadarTab({ onGoGundem }: { onGoGundem: () => void }) {
  const q = useQuery({
    queryKey: ["trend-ideas"],
    queryFn: fetchTrendIdeas,
    // Dogrulama surerken listeyi tazele
    refetchInterval: (query) => (query.state.data?.some((i) => i.status === "pending" || i.status === "checking" || i.designStatus === "generating") ? 8000 : false),
  });
  const m = useMutation({ mutationFn: ({ id, status }: { id: string; status: "dismissed" | "pending" }) => patchTrendIdea(id, status), onSuccess: () => void q.refetch() });
  const design = useMutation({
    mutationFn: (id: string) => api(`/api/trend-intelligence/ideas/${id}/design`, { method: "POST", body: "{}" }),
    onSuccess: () => void q.refetch(),
  });
  const calendarIdeas = useMutation({
    mutationFn: () => api<{ ideaCount: number; events: Array<{ name: string }> }>("/api/trend-intelligence/calendar-ideas", { method: "POST", body: "{}" }),
    onSuccess: () => void q.refetch(),
  });
  const [scanned, setScanned] = useState<Set<string>>(new Set());
  const scan = useMutation({
    // Eklentiye tek sayfalik Etsy aramasi (derin sekme yok: Etsy erisim kisiti)
    mutationFn: (i: TrendIdeaRow) => api("/api/tasks", { method: "POST", body: JSON.stringify({ type: "scan", payload: { keywords: [i.keyword], pages: 1, deep: false } }) }),
    onSuccess: (_d, i) => setScanned((prev) => new Set(prev).add(i.id)),
  });

  if (q.isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (q.isError) return <p className="text-sm text-red-600">{(q.error as Error).message}</p>;
  const ideas = q.data ?? [];
  const checking = ideas.filter((i) => i.status === "pending" || i.status === "checking").length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Radar className="h-4 w-4" /> Radar ({ideas.length})</h2>
          <p className="mt-1 text-xs text-gray-500">
            Gündem fikirleri Etsy talep/rekabet puanı ve USPTO marka kontrolünden geçip puana göre sıralanır. Puan = fırsat × aciliyet × marka riski.
            {checking > 0 && ` ${checking} fikir kontrol ediliyor...`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => calendarIdeas.mutate()} disabled={calendarIdeas.isPending}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {calendarIdeas.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />} Takvimden fikir üret
          </button>
          <button onClick={onGoGundem} className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
            <Newspaper className="h-4 w-4" /> Gündemden fikir üret
          </button>
        </div>
      </div>
      {calendarIdeas.data && (
        <p className="text-xs text-emerald-700">
          {calendarIdeas.data.ideaCount} fikir eklendi ({calendarIdeas.data.events.map((e) => e.name).join(", ")}); Etsy ve marka kontrolü sürüyor.
        </p>
      )}
      {calendarIdeas.isError && <p className="text-sm text-red-600">{(calendarIdeas.error as Error).message}</p>}

      <RadarSettingsPanel />
      {(design.isError || scan.isError) && <p className="text-sm text-red-600">{((design.error || scan.error) as Error).message}</p>}

      {ideas.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Henüz fikir yok. USA Gündem sekmesinde "Gündemi Tara" ile toplanan olaylardan fikir üretin.
        </div>
      )}

      <div className="space-y-2">
        {ideas.map((i) => {
          const pending = i.status === "pending" || i.status === "checking";
          return (
            <div key={i.id} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className={clsx("flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold",
                  i.score == null ? "bg-gray-100 text-gray-400" : i.score >= 50 ? "bg-emerald-50 text-emerald-700" : i.score >= 25 ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600")}>
                  {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : i.score ?? "–"}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">“{i.suggestedText}”</p>
                  <p className="mt-0.5 text-sm text-gray-700">{i.idea}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Olay: {i.eventTitle} · {i.productTypes.join(", ")}{i.trendWindow ? ` · ${i.trendWindow}` : ""}
                  </p>
                  {i.sources.length > 0 && <div className="mt-1"><SourceLinks sources={i.sources} /></div>}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-700">Etsy: {i.keyword}</span>
                    {i.status === "ready" && (
                      <span className="text-gray-600">
                        Talep {i.demandScore} · Rekabet {i.competitionScore} · Fırsat {i.opportunityScore}
                        {i.totalResults != null && ` · ${i.totalResults.toLocaleString("tr-TR")} sonuç`}
                      </span>
                    )}
                    {i.ipLevel && (
                      <span className={clsx("rounded-full px-2 py-0.5", IP_BADGE[i.ipLevel].cls)}
                        title={(i.ipHits ?? []).map((h) => `${h.phrase}: ${h.marks.slice(0, 3).map((mk) => `${mk.wordmark} (${mk.owner})`).join(", ")}`).join("\n")}>
                        {IP_BADGE[i.ipLevel].label}
                      </span>
                    )}
                    {i.status === "failed" && <span className="text-red-600">Kontrol başarısız: {i.error}</span>}
                  </div>
                  {(i.designs.length > 0 || i.designStatus === "generating" || i.designError) && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {i.designs.map((d) => (
                        <Link key={d.id} to="/designs" title={d.model ?? undefined} className="block h-20 w-20 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                          <img src={d.url.startsWith("http") ? d.url : `${getBackendUrl()}${d.url}`} alt={`“${i.suggestedText}” tasarımı`} className="h-full w-full object-contain" loading="lazy" />
                        </Link>
                      ))}
                      {i.designStatus === "generating" && <span className="flex items-center gap-1 text-xs text-gray-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Tasarım üretiliyor...</span>}
                      {i.designError && <span className="text-xs text-red-600">{i.designError}</span>}
                    </div>
                  )}
                  {i.performance && (
                    <p className="mt-2 text-xs text-gray-600">
                      {i.performance.drafts} taslak · {i.performance.published} yayında
                      {i.performance.published > 0 && ` · ${i.performance.views.toLocaleString("tr-TR")} görüntülenme · ${i.performance.favorites} favori · `}
                      {i.performance.published > 0 && <span className={i.performance.sales > 0 ? "font-semibold text-emerald-700" : ""}>{i.performance.sales} satış</span>}
                      {i.performance.listingIds.slice(0, 3).map((id) => (
                        <a key={id} href={`https://www.etsy.com/listing/${id}`} target="_blank" rel="noreferrer" className="ml-2 text-indigo-700 hover:underline">Etsy</a>
                      ))}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={() => design.mutate(i.id)} disabled={i.designStatus === "generating" || (design.isPending && design.variables === i.id)}
                      className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                      <Wand2 className="h-3.5 w-3.5" /> {i.designs.length ? "Yeniden tasarla" : "Tasarım üret"}
                    </button>
                    <button onClick={() => scan.mutate(i)} disabled={scanned.has(i.id)}
                      className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                      <Search className="h-3.5 w-3.5" /> {scanned.has(i.id) ? "Keşif görevi eklendi" : "Etsy'de tara (eklenti)"}
                    </button>
                  </div>
                </div>
                <div className="flex flex-shrink-0 gap-1">
                  {i.status === "failed" && (
                    <button onClick={() => m.mutate({ id: i.id, status: "pending" })} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" title="Yeniden kontrol et" aria-label="Yeniden kontrol et">
                      <RotateCw className="h-4 w-4" />
                    </button>
                  )}
                  <button onClick={() => m.mutate({ id: i.id, status: "dismissed" })} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" title="Gizle" aria-label="Gizle">
                    <EyeOff className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

async function fetchTrendEvents(): Promise<{ events: TrendEventRow[]; total: number }> {
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/events?hours=72`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Olaylar yüklenemedi");
  return (await res.json()).data;
}

async function generateAISuggestions(context?: string): Promise<{ suggestions: AISuggestion | null; rawContent?: string }> {
  const res = await fetch(`${getBackendUrl()}/api/trend-intelligence/ai-suggestions`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ context }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "AI analizi başarısız");
  }
  return (await res.json()).data;
}

// ========= Components =========

function PriorityBadge({ priority }: { priority: string }) {
  const colors: Record<string, string> = {
    high: "bg-red-100 text-red-700",
    medium: "bg-yellow-100 text-yellow-700",
    low: "bg-gray-100 text-gray-600",
  };
  const labels: Record<string, string> = { high: "Yüksek", medium: "Orta", low: "Düşük" };
  return (
    <span className={clsx("rounded-full px-2 py-0.5 text-[10px] font-medium", colors[priority] || colors.low)}>
      {labels[priority] || priority}
    </span>
  );
}

type TabId = "radar" | "calendar" | "jewelry" | "rising" | "hot" | "top-reviewed" | "themes" | "ai" | "gundem";

// ========= Main Page =========

// ─── Plan Picker Helper ──────────────────────────────

function AddToPlanButton({ items, label, onCreateNew }: {
  items: Array<{ type: string; title: string; content?: string; metadata?: Record<string, unknown> }>;
  label?: string;
  onCreateNew?: () => void;
}) {
  const [plans, setPlans] = useState<Array<{ id: string; title: string }>>([]);
  const [open, setOpen] = useState(false);
  const [added, setAdded] = useState(false);

  async function loadPlans() {
    try {
      const res = await fetch(`${getBackendUrl()}/api/plans?status=active`, { headers: getAuthHeaders() });
      const json = await res.json();
      setPlans(json.data || []);
    } catch { /* ignore */ }
  }

  async function addToPlan(planId: string) {
    try {
      await fetch(`${getBackendUrl()}/api/plans/${planId}/items/bulk`, {
        method: "POST", headers: getAuthHeaders(),
        body: JSON.stringify({ items }),
      });
      setAdded(true);
      setOpen(false);
      setTimeout(() => setAdded(false), 2000);
    } catch { /* ignore */ }
  }

  return (
    <div className="relative inline-block">
      <button onClick={(e) => { e.stopPropagation(); if (!open) loadPlans(); setOpen(!open); }}
        className={`flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg transition-colors ${
          added ? "bg-emerald-100 text-emerald-700" : "bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
        }`}>
        {added ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
        {added ? "Eklendi" : (label || "Plana Ekle")}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 w-48"
          onClick={e => e.stopPropagation()}>
          {onCreateNew && (
            <button onClick={() => { setOpen(false); onCreateNew(); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 flex items-center gap-2 text-emerald-700 border-b border-gray-100">
              <Plus className="h-3 w-3" /> Yeni Plan Oluştur
            </button>
          )}
          {plans.length === 0 && !onCreateNew ? (
            <p className="px-3 py-2 text-xs text-gray-400">Plan yok. Önce plan oluşturun.</p>
          ) : plans.length === 0 && onCreateNew ? (
            <p className="px-3 py-2 text-xs text-gray-400">Henüz mevcut plan yok</p>
          ) : plans.map(p => (
            <button key={p.id} onClick={() => addToPlan(p.id)}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 flex items-center gap-2">
              <ClipboardList className="h-3 w-3 text-indigo-400" /> {p.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Create Plan from Gundem Modal ──────────────────────────────

interface PlanItemDraft {
  type: string;
  title: string;
  content: string;
  enabled: boolean;
}

function CreatePlanModal({
  initialTitle,
  initialDescription,
  initialItems,
  initialPriority,
  planColor,
  onClose,
}: {
  initialTitle: string;
  initialDescription: string;
  initialItems: PlanItemDraft[];
  initialPriority?: "high" | "medium" | "low";
  planColor?: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);

  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [priority, setPriority] = useState<"high" | "medium" | "low">(initialPriority || "medium");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(nextWeek);
  const [color] = useState(planColor || "#6366f1");

  const [items, setItems] = useState<PlanItemDraft[]>(initialItems);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function toggleItem(idx: number) {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, enabled: !item.enabled } : item));
  }

  function removeItem(idx: number) {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }

  function updateItem(idx: number, field: "title" | "content", value: string) {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  }

  async function handleSave() {
    if (!title.trim()) { setError("Başlık zorunlu"); return; }
    setSaving(true);
    setError("");
    try {
      // Step 1: Create plan
      const planRes = await fetch(`${getBackendUrl()}/api/plans`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || null,
          color,
          priority,
          startDate: startDate || null,
          endDate: endDate || null,
        }),
      });
      if (!planRes.ok) {
        const err = await planRes.json();
        throw new Error(err.message || "Plan oluşturulamadı");
      }
      const plan = (await planRes.json()).data;

      // Step 2: Add enabled items
      const enabledItems = items.filter(i => i.enabled);
      if (enabledItems.length > 0) {
        await fetch(`${getBackendUrl()}/api/plans/${plan.id}/items/bulk`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            items: enabledItems.map(i => ({
              type: i.type,
              title: i.title,
              content: i.content,
            })),
          }),
        });
      }

      onClose();
      navigate("/plans");
    } catch (err: any) {
      setError(err.message || "Bir hata oluştu");
    } finally {
      setSaving(false);
    }
  }

  const enabledCount = items.filter(i => i.enabled).length;

  const priorityOptions = [
    { value: "high", label: "Yüksek", color: "text-red-600", bg: "bg-red-50 border-red-200" },
    { value: "medium", label: "Orta", color: "text-yellow-600", bg: "bg-yellow-50 border-yellow-200" },
    { value: "low", label: "Düşük", color: "text-gray-600", bg: "bg-gray-50 border-gray-200" },
  ];

  const typeLabels: Record<string, { label: string; color: string }> = {
    trend: { label: "Trend", color: "bg-orange-100 text-orange-700" },
    idea: { label: "Fikir", color: "bg-indigo-100 text-indigo-700" },
    note: { label: "Not", color: "bg-gray-100 text-gray-600" },
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-orange-600" />
            <h2 className="text-lg font-bold text-gray-900">Gündem Planı Oluştur</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Plan Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Plan Başlığı</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Açıklama</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={2}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-300 resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Öncelik</label>
              <div className="flex gap-2">
                {priorityOptions.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setPriority(opt.value as "high" | "medium" | "low")}
                    className={clsx(
                      "flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors",
                      priority === opt.value ? opt.bg + " ring-1 ring-offset-1" : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                    )}
                  >
                    <Flag className={clsx("h-3 w-3 inline mr-1", opt.color)} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Başlangıç</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm" />
              </div>
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Bitiş</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm" />
              </div>
            </div>
          </div>

          {/* Items List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">
                Plan Maddeleri ({enabledCount}/{items.length} seçili)
              </h3>
              <div className="flex gap-2">
                <button onClick={() => setItems(prev => prev.map(i => ({ ...i, enabled: true })))} className="text-[10px] text-indigo-600 hover:underline">
                  Tümünü Seç
                </button>
                <button onClick={() => setItems(prev => prev.map(i => ({ ...i, enabled: false })))} className="text-[10px] text-gray-500 hover:underline">
                  Seçimi Kaldır
                </button>
              </div>
            </div>

            <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
              {items.map((item, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    "rounded-lg border p-3 transition-colors",
                    item.enabled ? "bg-white border-gray-200" : "bg-gray-50 border-gray-100 opacity-60"
                  )}
                >
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={item.enabled}
                      onChange={() => toggleItem(idx)}
                      className="rounded mt-0.5 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={clsx("text-[10px] px-1.5 py-0.5 rounded font-medium", typeLabels[item.type]?.color || "bg-gray-100 text-gray-600")}>
                          {typeLabels[item.type]?.label || item.type}
                        </span>
                        <input
                          type="text"
                          value={item.title}
                          onChange={e => updateItem(idx, "title", e.target.value)}
                          className="flex-1 text-xs font-medium text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-indigo-400 focus:outline-none px-1 py-0.5"
                        />
                      </div>
                      <textarea
                        value={item.content}
                        onChange={e => updateItem(idx, "content", e.target.value)}
                        rows={2}
                        className="w-full text-[11px] text-gray-600 bg-transparent border rounded px-2 py-1 resize-none focus:ring-1 focus:ring-indigo-200 focus:border-indigo-300"
                      />
                    </div>
                    <button onClick={() => removeItem(idx)} className="p-1 hover:bg-red-50 rounded flex-shrink-0" title="Kaldır">
                      <X className="h-3.5 w-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t bg-gray-50 rounded-b-2xl">
          <p className="text-xs text-gray-500">{enabledCount} madde eklenecek</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
              İptal
            </button>
            <button
              onClick={handleSave}
              disabled={saving || enabledCount === 0}
              className="flex items-center gap-2 px-5 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {saving ? "Oluşturuluyor..." : "Planı Oluştur"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TrendIntelligencePage() {
  const [activeTab, setActiveTab] = useState<TabId>("radar");
  const { vertical } = useShopVertical();
  const jewelryQ = useQuery({ queryKey: ["trend-jewelry-ideas"], queryFn: fetchJewelryIdeas, enabled: vertical === "jewelry" && activeTab === "jewelry" });
  const [aiContext, setAiContext] = useState("");
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [planModal, setPlanModal] = useState<{
    title: string;
    description: string;
    items: PlanItemDraft[];
    priority?: "high" | "medium" | "low";
    color?: string;
  } | null>(null);

  // Top Reviewed tab state
  const [trDateFilter, setTrDateFilter] = useState<ReviewDateFilter>("all");
  const [trCustomStart, setTrCustomStart] = useState("");
  const [trCustomEnd, setTrCustomEnd] = useState("");
  const [trShowDaily, setTrShowDaily] = useState(false);

  const calendarQ = useQuery({ queryKey: ["trend-calendar"], queryFn: fetchCalendar });
  const risingQ = useQuery({ queryKey: ["trend-rising"], queryFn: fetchRisingProducts });
  const themesQ = useQuery({ queryKey: ["trend-themes"], queryFn: fetchThemes });
  const summaryQ = useQuery({ queryKey: ["trend-summary"], queryFn: fetchSummary });
  const topReviewedQ = useQuery({
    queryKey: ["trend-top-reviewed", trDateFilter, trCustomStart, trCustomEnd],
    queryFn: () => fetchTopReviewed(trDateFilter, trCustomStart, trCustomEnd),
  });
  const hotProductsQ = useQuery({ queryKey: ["trend-hot-products"], queryFn: fetchHotProducts });

  const aiM = useMutation({
    mutationFn: (ctx?: string) => generateAISuggestions(ctx),
  });

  const eventIdeasM = useMutation({
    mutationFn: (eventId: string) => api<{ ideaCount: number }>("/api/trend-intelligence/calendar-ideas", { method: "POST", body: JSON.stringify({ eventIds: [eventId] }) }),
  });

  const trendEventsQ = useQuery({ queryKey: ["trend-events"], queryFn: fetchTrendEvents, enabled: activeTab === "gundem" });

  const gundemM = useMutation({
    mutationFn: (params?: { categories?: string[]; productTypes?: string[] }) => fetchGundem(params),
    // Tablo bossa gundem once kaynaklari tarar; listeyi tazele
    onSuccess: () => void trendEventsQ.refetch(),
  });

  // Gundem filter state
  const [gundemCategories, setGundemCategories] = useState<Set<string>>(new Set());
  const [gundemProducts, setGundemProducts] = useState<Set<string>>(new Set(["t-shirt", "ornament"]));

  const GUNDEM_CATEGORIES = [
    { id: "news", label: "Haberler" },
    { id: "sports", label: "Spor" },
    { id: "entertainment", label: "Eğlence" },
    { id: "concerts", label: "Konser/Festival" },
    { id: "social", label: "Sosyal Medya" },
    { id: "seasonal", label: "Sezonsal" },
    { id: "popculture", label: "Pop Kültür" },
  ];

  const GUNDEM_PRODUCTS = [
    { id: "t-shirt", label: "Tişört" },
    { id: "sweatshirt", label: "Sweatshirt" },
    { id: "hoodie", label: "Hoodie" },
    { id: "mug", label: "Kupa" },
    { id: "sticker", label: "Sticker" },
    { id: "poster", label: "Poster" },
    { id: "tote bag", label: "Tote Bag" },
    { id: "phone case", label: "Telefon Kılıfı" },
    { id: "canvas", label: "Canvas" },
    { id: "pillow", label: "Yastık" },
    { id: "tumbler", label: "Tumbler" },
    { id: "ornament", label: "Ornament" },
  ];

  function toggleGundemCat(id: string) {
    setGundemCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleGundemProduct(id: string) {
    setGundemProducts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const tabs: Array<{ id: TabId; label: string; icon: typeof TrendingUp }> = [
    { id: "radar", label: "Radar", icon: Radar },
    { id: "calendar", label: "Sezon Takvimi", icon: Calendar },
    ...(vertical === "jewelry" ? [{ id: "jewelry" as TabId, label: "Takı Fikirleri", icon: Sparkles }] : []),
    { id: "rising", label: "Yükselen Ürünler", icon: TrendingUp },
    { id: "hot", label: "Sıcak Ürünler", icon: Flame },
    { id: "top-reviewed", label: "En Çok Yorumlanan", icon: MessageSquare },
    { id: "themes", label: "Tema Keşfi", icon: Tag },
    { id: "gundem", label: "USA Gündem", icon: Newspaper },
    { id: "ai", label: "AI Trend Bot", icon: Sparkles },
  ];

  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <TrendingUp className="h-6 w-6 text-indigo-500" />
          Trend Intelligence
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          AI destekli trend analizi, sezonsal temalar ve tasarım önerileri
        </p>
      </div>

      {/* Summary Stats */}
      {summaryQ.data && (
        <div className="mb-6 grid grid-cols-4 gap-3">
          {[
            { label: "Takip Edilen Mağaza", value: summaryQ.data.trackedShops, icon: ShoppingBag, color: "text-orange-600 bg-orange-50" },
            { label: "Takip Edilen Keyword", value: summaryQ.data.trackedKeywords, icon: Target, color: "text-blue-600 bg-blue-50" },
            { label: "Keşfedilen Trend", value: summaryQ.data.discoveredTrends, icon: Flame, color: "text-red-600 bg-red-50" },
            { label: "Bu Hafta Yeni Ürün", value: summaryQ.data.newProductsThisWeek, icon: Package, color: "text-emerald-600 bg-emerald-50" },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className={clsx("flex h-8 w-8 items-center justify-center rounded-lg", stat.color)}>
                  <stat.icon className="h-4 w-4" />
                </div>
                <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
              </div>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 flex items-center gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              "flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors flex-1 justify-center",
              activeTab === tab.id
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ====================== TAKI FIKIRLERI ====================== */}
      {activeTab === "jewelry" && (
        <div className="space-y-4">
          <p className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-xs text-indigo-800">
            Yaklaşan özel günlerin takıdaki karşılığı ve kataloğundaki uygun ürünler. Kalabalık bir kelime yerine özel güne bağlı niş başlık (ör. "Red White Blue Birthstone Necklace") rakipten ayrıştırır.
          </p>
          {jewelryQ.isLoading && <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>}
          {jewelryQ.data?.length === 0 && <p className="text-sm text-gray-500">Önümüzdeki 120 günde takıya uygun özel gün yok.</p>}
          {jewelryQ.data?.map((ev) => (
            <div key={ev.name + ev.date} className="rounded-xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">{ev.name}</h3>
                <span className="text-xs text-gray-500">{ev.daysUntil != null && ev.daysUntil >= 0 ? `${ev.daysUntil} gün sonra` : "geçti"}</span>
              </div>
              <div className="divide-y divide-gray-100">
                {ev.angles.map((a) => (
                  <div key={a.title} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-gray-800">{a.title}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{a.note}</p>
                      </div>
                      <span className={clsx("shrink-0 rounded-full px-2 py-0.5 text-xs", a.matchCount ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500")}>
                        {a.matchCount ? `${a.matchCount} uygun ürün` : "katalogda yok"}
                      </span>
                    </div>
                    {a.examples.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {a.examples.map((p) => (
                          <Link key={p.catalogId + p.categoryId} to={`/listing-builder/jewelry?catalogId=${p.catalogId}&categoryId=${p.categoryId}`}
                            className="max-w-[240px] truncate rounded-lg border border-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:border-indigo-300 hover:text-indigo-700" title={p.name}>
                            {p.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ====================== CALENDAR TAB ====================== */}
      {activeTab === "radar" && <RadarTab onGoGundem={() => setActiveTab("gundem")} />}

      {activeTab === "calendar" && (
        <div className="space-y-4">
          {calendarQ.isLoading && (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          )}

          {calendarQ.data && (
            <>
              {/* Current Season */}
              <div className="rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-indigo-900">
                      Aktif Sezon: {calendarQ.data.currentSeason}
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {calendarQ.data.seasonKeywords.map((kw) => (
                        <span key={kw} className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Calendar className="h-10 w-10 text-indigo-300" />
                </div>
              </div>

              {/* Upcoming Events */}
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700">Yaklaşan Etkinlikler ({calendarQ.data.events.length})</h4>
                <button
                  onClick={() => {
                    const items: PlanItemDraft[] = [];
                    calendarQ.data!.events.forEach(ev => {
                      items.push({ type: "trend", title: ev.name, content: `Kategori: ${ev.category} | ${ev.daysUntil}g sonra | Öncelik: ${ev.priority}`, enabled: true });
                      ev.designIdeas.forEach(idea => items.push({ type: "idea", title: idea, content: `Etkinlik: ${ev.name}`, enabled: true }));
                    });
                    setPlanModal({ title: `Sezon Takvimi - ${calendarQ.data!.currentSeason}`, description: `Sezon: ${calendarQ.data!.currentSeason}\nAnahtar Kelimeler: ${calendarQ.data!.seasonKeywords.join(", ")}`, items, priority: "medium", color: "#6366f1" });
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700"
                >
                  <ClipboardList className="h-3.5 w-3.5" /> Tümünü Plana Ekle
                </button>
              </div>
              <div className="space-y-2">
                {calendarQ.data.events.map((event) => {
                  const isExpanded = expandedEvent === event.id;
                  const isImminent = (event.daysUntil ?? 99) <= 14;
                  const isPast = (event.daysUntil ?? 0) < 0;

                  return (
                    <div
                      key={event.id}
                      className={clsx(
                        "rounded-xl border bg-white transition-shadow cursor-pointer",
                        isImminent ? "border-orange-200 shadow-sm" : "border-gray-200",
                        isPast ? "opacity-60" : ""
                      )}
                      onClick={() => setExpandedEvent(isExpanded ? null : event.id)}
                    >
                      <div className="flex items-center justify-between p-4">
                        <div className="flex items-center gap-3">
                          <div className={clsx(
                            "flex h-10 w-10 items-center justify-center rounded-lg text-sm font-bold",
                            isImminent ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-600"
                          )}>
                            {event.daysUntil !== undefined ? (
                              event.daysUntil < 0 ? `${Math.abs(event.daysUntil)}g` : `${event.daysUntil}g`
                            ) : "?"}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-semibold text-gray-900">{event.name}</h4>
                              <PriorityBadge priority={event.priority} />
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">{event.category}</span>
                            </div>
                            <p className="mt-0.5 text-xs text-gray-500">
                              {new Date(event.date + "T12:00:00Z").toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                              {" · "}{event.products.map((p) => (p === "tshirt" ? "Tişört" : "Ornament")).join(", ")}
                              {" · "}
                              {event.listByDays >= 0
                                ? (event.listByDays === 0 ? "listelemek için bugün son gün" : `listelemek için ${event.listByDays} gün var`)
                                : <span className="text-amber-700">listeleme penceresi geçti</span>}
                            </p>
                            <div className="mt-0.5 flex flex-wrap gap-1">
                              {event.keywords.slice(0, 4).map((kw) => (
                                <span key={kw} className="text-[10px] text-gray-400">#{kw}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isImminent && !isPast && (
                            <span className="flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                              <Zap className="h-3 w-3" />
                              Yaklaşan!
                            </span>
                          )}
                          <ChevronRight className={clsx("h-4 w-4 text-gray-400 transition-transform", isExpanded && "rotate-90")} />
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1.5">Anahtar Kelimeler</p>
                              <div className="flex flex-wrap gap-1">
                                {event.keywords.map((kw) => (
                                  <span key={kw} className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">{kw}</span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1.5">Tasarım Fikirleri</p>
                              <ul className="space-y-1">
                                {event.designIdeas.map((idea, i) => (
                                  <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                                    <Lightbulb className="h-3 w-3 text-yellow-500 mt-0.5 flex-shrink-0" />
                                    {idea}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                            {eventIdeasM.variables === event.id && eventIdeasM.data && (
                              <button onClick={(e) => { e.stopPropagation(); setActiveTab("radar"); }} className="text-xs text-emerald-700 hover:underline">
                                {eventIdeasM.data.ideaCount} fikir Radar'a eklendi →
                              </button>
                            )}
                            {eventIdeasM.variables === event.id && eventIdeasM.isError && (
                              <span className="text-xs text-red-600">{(eventIdeasM.error as Error).message}</span>
                            )}
                            {event.products.length > 0 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); eventIdeasM.mutate(event.id); }}
                                disabled={eventIdeasM.isPending}
                                className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300"
                              >
                                {eventIdeasM.isPending && eventIdeasM.variables === event.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radar className="h-3.5 w-3.5" />}
                                Bu olaydan fikir üret
                              </button>
                            )}
                            <button
                              onClick={() => setPlanModal({
                                title: event.name,
                                description: `Kategori: ${event.category} | ${event.daysUntil}g sonra`,
                                priority: event.priority as "high" | "medium" | "low",
                                color: "#6366f1",
                                items: [
                                  { type: "trend", title: event.name, content: `Kategori: ${event.category}, ${event.daysUntil}g sonra\nÖncelik: ${event.priority}`, enabled: true },
                                  ...event.keywords.map(kw => ({ type: "keyword" as string, title: kw, content: `Etkinlik: ${event.name}`, enabled: true })),
                                  ...event.designIdeas.map(idea => ({ type: "idea" as string, title: idea, content: `Etkinlik: ${event.name}`, enabled: true })),
                                ],
                              })}
                              className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                            >
                              <FileText className="h-3 w-3" /> Plana Ekle
                            </button>
                            <AddToPlanButton items={[
                              { type: "trend", title: event.name, content: `Kategori: ${event.category}, ${event.daysUntil}g sonra`, metadata: { priority: event.priority, category: event.category, daysUntil: event.daysUntil } },
                              ...event.keywords.map(kw => ({ type: "keyword" as const, title: kw, content: `Etkinlik: ${event.name}` })),
                              ...event.designIdeas.map(idea => ({ type: "idea" as const, title: idea, content: `Etkinlik: ${event.name}` })),
                            ]} label="Mevcut Plana" onCreateNew={() => setPlanModal({
                              title: event.name,
                              description: `Kategori: ${event.category} | ${event.daysUntil}g sonra`,
                              priority: event.priority as "high" | "medium" | "low",
                              color: "#6366f1",
                              items: [
                                { type: "trend", title: event.name, content: `Kategori: ${event.category}, ${event.daysUntil}g sonra`, enabled: true },
                                ...event.keywords.map(kw => ({ type: "keyword" as string, title: kw, content: `Etkinlik: ${event.name}`, enabled: true })),
                                ...event.designIdeas.map(idea => ({ type: "idea" as string, title: idea, content: `Etkinlik: ${event.name}`, enabled: true })),
                              ],
                            })} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ====================== RISING PRODUCTS TAB ====================== */}
      {activeTab === "rising" && (
        <div className="space-y-6">
          {risingQ.isLoading && (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          )}

          {risingQ.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <p className="text-sm text-red-700">{(risingQ.error as Error).message}</p>
            </div>
          )}

          {risingQ.data && (
            <>
              {/* Rising Products */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <ArrowUpRight className="h-5 w-5 text-emerald-500" />
                    Yükselen Ürünler
                    <span className="text-sm font-normal text-gray-400">Son 14 gün</span>
                  </h3>
                  {risingQ.data.rising.length > 0 && (
                    <button
                      onClick={() => setPlanModal({
                        title: `Yükselen Ürünler - ${new Date().toLocaleDateString("tr-TR")}`,
                        description: "Son 14 günde en çok büyüyen ürünler",
                        priority: "medium",
                        color: "#10b981",
                        items: risingQ.data!.rising.slice(0, 20).map(p => ({
                          type: "trend",
                          title: `${p.title} (${p.shopName})`,
                          content: `Fiyat: $${Number(p.price || 0).toFixed(2)} | Favori: +${p.favoriteDelta} | Yorum: +${p.reviewDelta} | Skor: ${p.growthScore}${p.category ? `\nKategori: ${p.category}` : ""}`,
                          enabled: true,
                        })),
                      })}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700"
                    >
                      <ClipboardList className="h-3.5 w-3.5" /> Plana Ekle
                    </button>
                  )}
                </div>

                {risingQ.data.rising.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <TrendingUp className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Henüz yeterli snapshot verisi yok. Mağazaları extension ile tarayın.</p>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    {risingQ.data.rising.slice(0, 20).map((product, idx) => (
                      <div key={product.id} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 hover:shadow-sm transition-shadow">
                        {/* Rank */}
                        <div className={clsx(
                          "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-xs font-bold",
                          idx < 3 ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"
                        )}>
                          #{idx + 1}
                        </div>

                        {/* Image */}
                        <div className="h-12 w-12 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden">
                          {product.imageUrl ? (
                            <img src={product.imageUrl} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Package className="h-5 w-5 text-gray-300" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{product.title}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                            <span>{product.shopName}</span>
                            {product.price && <span>${Number(product.price).toFixed(2)}</span>}
                            {product.category && (
                              <span className="rounded bg-gray-100 px-1 py-0.5 text-[10px]">{product.category}</span>
                            )}
                          </div>
                        </div>

                        {/* Growth Indicators */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {product.favoriteDelta > 0 && (
                            <div className="flex items-center gap-1 text-pink-600">
                              <Heart className="h-3.5 w-3.5" />
                              <span className="text-xs font-medium">+{product.favoriteDelta}</span>
                            </div>
                          )}
                          {product.reviewDelta > 0 && (
                            <div className="flex items-center gap-1 text-emerald-600">
                              <Star className="h-3.5 w-3.5" />
                              <span className="text-xs font-medium">+{product.reviewDelta}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1 text-indigo-600 bg-indigo-50 rounded-full px-2 py-0.5">
                            <ArrowUp className="h-3 w-3" />
                            <span className="text-[10px] font-bold">{product.growthScore}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* New Products Timeline */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
                  <Package className="h-5 w-5 text-blue-500" />
                  Yeni Eklenen Ürünler
                  <span className="text-sm font-normal text-gray-400">Son 14 gün</span>
                </h3>

                {risingQ.data.newProducts.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Son 14 günde yeni ürün eklenmemiş.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {risingQ.data.newProducts.slice(0, 20).map((product) => (
                      <div key={product.id} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
                        <div className="h-14 w-14 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden">
                          {product.imageUrl ? (
                            <img src={product.imageUrl} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Package className="h-5 w-5 text-gray-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{product.title}</p>
                          <p className="text-xs text-gray-500">{product.shopName}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {product.price && <span className="text-xs font-medium text-gray-700">${Number(product.price).toFixed(2)}</span>}
                            <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                              <Clock className="h-3 w-3" />
                              {new Date(product.firstSeenAt).toLocaleDateString("tr-TR", { day: "numeric", month: "short" })}
                            </span>
                          </div>
                          {product.tags.length > 0 && (
                            <div className="flex flex-wrap gap-0.5 mt-1">
                              {product.tags.slice(0, 3).map((tag) => (
                                <span key={tag} className="text-[9px] text-gray-400">#{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ====================== HOT PRODUCTS TAB ====================== */}
      {activeTab === "hot" && (
        <div className="space-y-4">
          {hotProductsQ.isLoading && (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          )}

          {hotProductsQ.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <p className="text-sm text-red-700">{(hotProductsQ.error as Error).message}</p>
            </div>
          )}

          {hotProductsQ.data && (
            <>
              <div className="rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-red-50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Flame className="h-5 w-5 text-orange-500" />
                  <h3 className="text-sm font-semibold text-orange-900">Sıcak Ürünler</h3>
                </div>
                <p className="text-xs text-orange-700">
                  Takip edilen mağazalardaki sepete eklenen ve son 24 saatte satın alınan ürünler.
                  Extension ile mağaza taramasında otomatik toplanır.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Store className="h-4 w-4" />
                    {hotProductsQ.data.shopCount} mağaza
                  </span>
                  <span className="flex items-center gap-1">
                    <Flame className="h-4 w-4" />
                    {hotProductsQ.data.products.length} sıcak ürün
                  </span>
                </div>
                {hotProductsQ.data.products.length > 0 && (
                  <button
                    onClick={() => setPlanModal({
                      title: `Sıcak Ürünler - ${new Date().toLocaleDateString("tr-TR")}`,
                      description: `${hotProductsQ.data!.products.length} sıcak ürün, ${hotProductsQ.data!.shopCount} mağaza`,
                      priority: "high",
                      color: "#ef4444",
                      items: hotProductsQ.data!.products.map(p => ({
                        type: "trend",
                        title: `${p.title} (${p.shopName})`,
                        content: `Fiyat: $${Number(p.price || 0).toFixed(2)}${p.inCarts ? ` | ${p.inCarts} sepet` : ""}${p.recentPurchases ? ` | ${p.recentPurchases} satış/24s` : ""} | Favori: ${p.favoriteCount} | Yorum: ${p.reviewCount}`,
                        enabled: true,
                      })),
                    })}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700"
                  >
                    <ClipboardList className="h-3.5 w-3.5" /> Plana Ekle
                  </button>
                )}
              </div>

              {hotProductsQ.data.products.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Flame className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Henüz sıcak ürün verisi yok</p>
                  <p className="text-xs mt-1">Extension ile mağaza taramasından sonra bu veriler otomatik toplanır</p>
                </div>
              ) : (
                <div className="grid gap-2">
                  {hotProductsQ.data.products.map((product, idx) => (
                    <div key={product.id} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 hover:shadow-sm transition-shadow">
                      {/* Rank */}
                      <div className={clsx(
                        "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-xs font-bold",
                        idx < 3 ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-500"
                      )}>
                        #{idx + 1}
                      </div>

                      {/* Image */}
                      <div className="h-12 w-12 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden">
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <Package className="h-5 w-5 text-gray-300" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <a
                          href={product.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-gray-900 hover:text-indigo-600 truncate block"
                        >
                          {product.title}
                        </a>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                          <span>{product.shopName}</span>
                          {product.price && <span>${Number(product.price).toFixed(2)}</span>}
                        </div>
                      </div>

                      {/* Hot Metrics */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {product.inCarts != null && product.inCarts > 0 && (
                          <div className="flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-700">
                            <ShoppingBag className="h-3.5 w-3.5" />
                            {product.inCarts} sepet
                          </div>
                        )}
                        {product.recentPurchases != null && product.recentPurchases > 0 && (
                          <div className="flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
                            <Zap className="h-3.5 w-3.5" />
                            {product.recentPurchases} satış/24s
                          </div>
                        )}
                      </div>

                      {/* Extra stats */}
                      <div className="flex items-center gap-2 flex-shrink-0 text-xs text-gray-400">
                        {product.favoriteCount > 0 && (
                          <span className="flex items-center gap-0.5">
                            <Heart className="h-3 w-3" /> {product.favoriteCount}
                          </span>
                        )}
                        {product.reviewCount > 0 && (
                          <span className="flex items-center gap-0.5">
                            <Star className="h-3 w-3" /> {product.reviewCount}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Last scan info */}
              {hotProductsQ.data.products.length > 0 && hotProductsQ.data.products[0].hotMetricsAt && (
                <p className="text-xs text-gray-400 text-center">
                  Son tarama: {new Date(hotProductsQ.data.products[0].hotMetricsAt).toLocaleString("tr-TR")}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* ====================== TOP REVIEWED TAB ====================== */}
      {activeTab === "top-reviewed" && (
        <div className="space-y-4">
          {/* Date Filters */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              <Filter className="h-3.5 w-3.5 text-gray-400 mr-1" />
              {([
                ["all", "Tümü"],
                ["this_week", "Bu Hafta"],
                ["this_month", "Bu Ay"],
                ["last_month", "Geçen Ay"],
                ["last_3_months", "Son 3 Ay"],
                ["last_6_months", "Son 6 Ay"],
                ["custom", "Özel"],
              ] as [ReviewDateFilter, string][]).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTrDateFilter(key)}
                  className={clsx(
                    "rounded-lg px-2.5 py-1 text-xs font-medium transition-colors",
                    trDateFilter === key
                      ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
                      : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                  )}
                >
                  {label}
                </button>
              ))}

              <button
                onClick={() => setTrShowDaily(!trShowDaily)}
                className={clsx(
                  "ml-auto rounded-lg px-2.5 py-1 text-xs font-medium transition-colors border",
                  trShowDaily
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                )}
              >
                {trShowDaily ? "Günlük" : "Aylık"} Grafik
              </button>
            </div>

            {trDateFilter === "custom" && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 border border-gray-200">
                <span className="text-xs text-gray-500">Başlangıç:</span>
                <input
                  type="month"
                  value={trCustomStart}
                  onChange={(e) => setTrCustomStart(e.target.value)}
                  className="h-7 rounded border border-gray-300 px-2 text-xs text-gray-700 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                />
                <span className="text-xs text-gray-500">Bitiş:</span>
                <input
                  type="month"
                  value={trCustomEnd}
                  onChange={(e) => setTrCustomEnd(e.target.value)}
                  className="h-7 rounded border border-gray-300 px-2 text-xs text-gray-700 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                />
              </div>
            )}
          </div>

          {topReviewedQ.isLoading && (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          )}

          {topReviewedQ.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <p className="text-sm text-red-700">{(topReviewedQ.error as Error).message}</p>
            </div>
          )}

          {topReviewedQ.data && (
            <>
              {/* Stats bar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Store className="h-4 w-4" />
                    {topReviewedQ.data.shopCount} mağaza
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageSquare className="h-4 w-4" />
                    {topReviewedQ.data.totalReviews} toplam yorum
                  </span>
                  <span className="flex items-center gap-1">
                    <Package className="h-4 w-4" />
                    {topReviewedQ.data.listings.length} ürün
                  </span>
                </div>
                {topReviewedQ.data.listings.length > 0 && (
                  <button
                    onClick={() => setPlanModal({
                      title: `En Çok Yorumlanan - ${new Date().toLocaleDateString("tr-TR")}`,
                      description: `${topReviewedQ.data!.totalReviews} yorum, ${topReviewedQ.data!.listings.length} ürün`,
                      priority: "medium",
                      color: "#8b5cf6",
                      items: topReviewedQ.data!.listings.map(l => ({
                        type: "trend",
                        title: `${l.title} (${l.shopName})`,
                        content: `Yorum: ${l.count} | Puan: ${l.avgRating.toFixed(1)}${l.url ? `\nURL: ${l.url}` : ""}`,
                        enabled: true,
                      })),
                    })}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-medium hover:bg-violet-700"
                  >
                    <ClipboardList className="h-3.5 w-3.5" /> Plana Ekle
                  </button>
                )}
              </div>

              {topReviewedQ.data.listings.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Bu dönemde yorum bulunamadı</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {topReviewedQ.data.listings.map((item, i) => (
                    <div
                      key={`${item.url ?? item.title}-${i}`}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-center gap-3">
                        {/* Rank */}
                        <span className={clsx(
                          "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold",
                          i < 3 ? "bg-yellow-100 text-yellow-700" : "bg-indigo-50 text-indigo-600"
                        )}>
                          {i + 1}
                        </span>

                        {/* Image */}
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt=""
                            className="h-11 w-11 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50">
                            <ShoppingBag className="h-4 w-4 text-gray-300" />
                          </div>
                        )}

                        {/* Info */}
                        <div className="min-w-0 flex-1">
                          {item.url ? (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-gray-900 hover:text-indigo-600 line-clamp-1"
                            >
                              {item.title}
                            </a>
                          ) : (
                            <p className="text-sm font-medium text-gray-900 line-clamp-1">{item.title}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-0.5">{item.shopName}</p>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {item.avgRating > 0 && (
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                              {item.avgRating.toFixed(1)}
                            </span>
                          )}
                          <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 tabular-nums">
                            {item.count} yorum
                          </span>
                        </div>
                      </div>

                      {/* Mini chart */}
                      {(() => {
                        const chartData = trShowDaily && item.daily && item.daily.length > 0
                          ? item.daily
                          : item.monthly && item.monthly.length > 1
                            ? item.monthly
                            : null;
                        if (!chartData) return null;
                        const isDaily = trShowDaily && item.daily && item.daily.length > 0;
                        const maxC = Math.max(...chartData.map((m) => m.count));
                        return (
                          <div className="mt-2 ml-10 flex items-end gap-1 overflow-x-auto">
                            {chartData.map((m) => {
                              const h = maxC > 0 ? Math.max((m.count / maxC) * 28, 4) : 4;
                              return (
                                <div key={m.key} className="flex flex-col items-center gap-0.5 min-w-[20px]">
                                  <span className="text-[9px] font-medium text-gray-500 tabular-nums">{m.count}</span>
                                  <div
                                    className={`w-3 rounded-t-sm ${isDaily ? "bg-emerald-300" : "bg-indigo-300"}`}
                                    style={{ height: `${h}px` }}
                                  />
                                  <span className="text-[8px] text-gray-400 whitespace-nowrap">{m.label.split(" ")[0]}</span>
                                  {isDaily && "dayName" in m && (
                                    <span className="text-[7px] text-gray-300">{(m as any).dayName}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ====================== THEMES TAB ====================== */}
      {activeTab === "themes" && (
        <div className="space-y-6">
          {themesQ.isLoading && (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          )}

          {themesQ.data && (
            <>
              {/* Stats */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>{themesQ.data.totalShops} mağazadan</span>
                  <span>{themesQ.data.totalProducts} ürün analiz edildi</span>
                </div>
                {themesQ.data.popularThemes.length > 0 && (
                  <button
                    onClick={() => {
                      const items: PlanItemDraft[] = [];
                      themesQ.data!.popularThemes.slice(0, 20).forEach(t => {
                        items.push({ type: "trend", title: `Tema: ${t.theme}`, content: `${t.productCount} ürün | Ort. Favori: ${t.avgFavorites} | Ort. Yorum: ${t.avgReviews} | Skor: ${t.score}`, enabled: true });
                      });
                      themesQ.data!.emergingThemes.forEach(t => {
                        items.push({ type: "idea", title: `Yükselen: ${t.theme}`, content: `${t.count} ürün (son 14 gün)`, enabled: true });
                      });
                      setPlanModal({
                        title: `Tema Analizi - ${new Date().toLocaleDateString("tr-TR")}`,
                        description: `${themesQ.data!.totalShops} mağazadan ${themesQ.data!.totalProducts} ürün analiz edildi`,
                        priority: "medium",
                        color: "#f59e0b",
                        items,
                      });
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700"
                  >
                    <ClipboardList className="h-3.5 w-3.5" /> Plana Ekle
                  </button>
                )}
              </div>

              {/* Popular Themes */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
                  <Flame className="h-5 w-5 text-orange-500" />
                  Popüler Temalar
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {themesQ.data.popularThemes.slice(0, 20).map((theme, idx) => (
                    <div key={theme.theme} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className={clsx(
                          "text-xs font-bold w-5",
                          idx < 3 ? "text-orange-600" : "text-gray-400"
                        )}>
                          {idx + 1}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{theme.theme}</p>
                          <p className="text-[10px] text-gray-400">
                            {theme.productCount} ürün
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-0.5 text-pink-500">
                          <Heart className="h-3 w-3" /> {theme.avgFavorites}
                        </span>
                        <span className="flex items-center gap-0.5 text-emerald-500">
                          <Star className="h-3 w-3" /> {theme.avgReviews}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Emerging Themes */}
              {themesQ.data.emergingThemes.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
                    <Zap className="h-5 w-5 text-yellow-500" />
                    Yeni Yükselen Temalar
                    <span className="text-sm font-normal text-gray-400">Son 14 gün</span>
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {themesQ.data.emergingThemes.map((theme) => (
                      <span
                        key={theme.theme}
                        className="rounded-full border border-yellow-200 bg-yellow-50 px-3 py-1.5 text-sm text-yellow-800"
                      >
                        {theme.theme}
                        <span className="ml-1.5 text-xs text-yellow-600">({theme.count})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Category Distribution */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
                  <BarChart3 className="h-5 w-5 text-blue-500" />
                  Kategori Dağılımı
                </h3>
                <div className="space-y-2">
                  {themesQ.data.categories.slice(0, 10).map((cat) => {
                    const maxCount = themesQ.data?.categories[0]?.count || 1;
                    const pct = (cat.count / maxCount) * 100;
                    return (
                      <div key={cat.name} className="flex items-center gap-3">
                        <span className="text-sm text-gray-700 w-40 truncate">{cat.name}</span>
                        <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 w-10 text-right">{cat.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ====================== GUNDEM TAB ====================== */}
      {activeTab === "gundem" && (
        <div className="space-y-6">
          {/* Header & Trigger */}
          <div className="rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Newspaper className="h-5 w-5 text-orange-600" />
              <h3 className="text-base font-semibold text-orange-900">USA Gündem → Etsy Fikirler</h3>
            </div>
            <p className="text-sm text-orange-700 mb-4">
              Google Trends (ABD aramaları), Wikipedia (ABD'de en çok okunanlar) ve Ticketmaster'dan (ABD turneleri)
              saatlik toplanan gerçek olaylardan Etsy'de satılabilir tasarım fikirleri üretir. Listede olmayan olay önerilmez.
            </p>

            {/* Category Filter */}
            <div className="mb-3">
              <p className="text-xs font-medium text-orange-800 mb-1.5">Kategoriler (boş = tümü)</p>
              <div className="flex flex-wrap gap-1.5">
                {GUNDEM_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => toggleGundemCat(cat.id)}
                    className={clsx(
                      "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                      gundemCategories.has(cat.id)
                        ? "bg-orange-600 text-white border-orange-600"
                        : "bg-white text-orange-700 border-orange-300 hover:bg-orange-50"
                    )}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Product Type Filter */}
            <div className="mb-4">
              <p className="text-xs font-medium text-orange-800 mb-1.5">Ürün Türleri (boş = tümü)</p>
              <div className="flex flex-wrap gap-1.5">
                {GUNDEM_PRODUCTS.map(pt => (
                  <button
                    key={pt.id}
                    onClick={() => toggleGundemProduct(pt.id)}
                    className={clsx(
                      "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                      gundemProducts.has(pt.id)
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                    )}
                  >
                    <ShoppingCart className="h-3 w-3 inline mr-0.5" />{pt.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => gundemM.mutate({
                categories: gundemCategories.size > 0 ? [...gundemCategories] : undefined,
                productTypes: gundemProducts.size > 0 ? [...gundemProducts] : undefined,
              })}
              disabled={gundemM.isPending}
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {gundemM.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Globe className="h-4 w-4" />
              )}
              Gündemi Tara
            </button>
            {gundemM.isError && (
              <p className="mt-2 text-sm text-red-600">{(gundemM.error as Error).message}</p>
            )}
          </div>

          {/* Toplanan gercek olaylar */}
          {trendEventsQ.data && (
            <details className="rounded-xl border border-gray-200 bg-white">
              <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-gray-700">
                Son 72 saatte toplanan olaylar ({trendEventsQ.data.total})
              </summary>
              <ul className="divide-y divide-gray-100 border-t border-gray-100">
                {trendEventsQ.data.events.slice(0, 60).map((ev) => (
                  <li key={ev.id} className="px-4 py-2">
                    <p className="text-sm text-gray-900">{ev.title}</p>
                    <SourceLinks sources={ev.sources} />
                  </li>
                ))}
                {trendEventsQ.data.total === 0 && (
                  <li className="px-4 py-3 text-sm text-gray-500">Henüz olay toplanmadı; kaynaklar saatte bir taranır.</li>
                )}
              </ul>
            </details>
          )}

          {/* Loading */}
          {gundemM.isPending && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-10 w-10 animate-spin text-orange-500 mb-3" />
              <p className="text-sm text-gray-500">Toplanan olaylardan fikirler üretiliyor...</p>
              <p className="text-xs text-gray-400 mt-1">Bu işlem 15-30 saniye sürebilir</p>
            </div>
          )}

          {/* Results */}
          {gundemM.data?.gundem && (
            <div className="space-y-6">
              {/* Summary + Plan Button */}
              {gundemM.data.gundem.summary && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm text-amber-900 font-medium flex-1">{gundemM.data.gundem.summary}</p>
                    <button
                      onClick={() => {
                        const g = gundemM.data!.gundem!;
                        const items: PlanItemDraft[] = [];
                        (g.quickWins || []).forEach(win => items.push({ type: "idea", title: `Hızlı Kazanç: ${win.slice(0, 80)}`, content: win, enabled: true }));
                        (g.trendingEvents || []).forEach(ev => {
                          items.push({ type: "trend", title: ev.event, content: `${ev.description}\n\nNeden Satar: ${ev.whyItSells}\nKategori: ${ev.category}\nTrend: ${ev.trendWindow}`, enabled: true });
                          (ev.designIdeas || []).forEach(idea => items.push({ type: "idea", title: idea.idea, content: `Metin: "${idea.suggestedText}"\nÜrünler: ${idea.productTypes?.join(", ")}\nKitle: ${idea.targetAudience}`, enabled: true }));
                        });
                        setPlanModal({ title: `USA Gündem - ${new Date().toLocaleDateString("tr-TR")}`, description: g.summary || "", items, priority: g.trendingEvents?.some(e => e.urgency === "high") ? "high" : "medium", color: "#f97316" });
                      }}
                      className="flex items-center gap-1.5 px-3 py-2 bg-orange-600 text-white rounded-lg text-xs font-medium hover:bg-orange-700 flex-shrink-0 shadow-sm"
                    >
                      <ClipboardList className="h-3.5 w-3.5" />
                      Tümünü Plana Ekle
                    </button>
                  </div>
                </div>
              )}

              {/* Quick Wins */}
              {gundemM.data.gundem.quickWins && gundemM.data.gundem.quickWins.length > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                  <h3 className="text-base font-semibold text-emerald-900 flex items-center gap-2 mb-3">
                    <Zap className="h-5 w-5" />
                    Hızlı Kazançlar
                  </h3>
                  <ol className="space-y-2">
                    {gundemM.data.gundem.quickWins.map((win: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-emerald-800">
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-200 text-xs font-bold text-emerald-700">
                          {idx + 1}
                        </span>
                        {win}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Trending Events */}
              {gundemM.data.gundem.trendingEvents && gundemM.data.gundem.trendingEvents.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-orange-600" />
                    Guncel Olaylar & Etsy Fikirler ({gundemM.data.gundem.trendingEvents.length})
                  </h3>
                  {gundemM.data.gundem.trendingEvents.map((ev: GundemEvent, idx: number) => {
                    const catColors: Record<string, string> = {
                      news: "bg-red-100 text-red-700",
                      sports: "bg-blue-100 text-blue-700",
                      entertainment: "bg-purple-100 text-purple-700",
                      concerts: "bg-pink-100 text-pink-700",
                      social: "bg-cyan-100 text-cyan-700",
                      seasonal: "bg-green-100 text-green-700",
                      popculture: "bg-indigo-100 text-indigo-700",
                    };
                    const catLabels: Record<string, string> = {
                      news: "Haber",
                      sports: "Spor",
                      entertainment: "Eğlence",
                      concerts: "Konser/Festival",
                      social: "Sosyal Medya",
                      seasonal: "Sezonsal",
                      popculture: "Pop Kültür",
                    };
                    const urgencyColors: Record<string, string> = {
                      high: "border-l-red-500",
                      medium: "border-l-yellow-500",
                      low: "border-l-gray-400",
                    };
                    return (
                      <div
                        key={idx}
                        className={clsx(
                          "rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden border-l-4",
                          urgencyColors[ev.urgency] || "border-l-gray-300"
                        )}
                      >
                        <div className="p-5">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h4 className="text-sm font-bold text-gray-900">{ev.event}</h4>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={clsx("rounded-full px-2 py-0.5 text-[10px] font-medium", catColors[ev.category] || "bg-gray-100 text-gray-600")}>
                                {catLabels[ev.category] || ev.category}
                              </span>
                              <span className="text-[10px] text-gray-400">
                                <Clock className="h-3 w-3 inline mr-0.5" />{ev.trendWindow}
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">{ev.description}</p>
                          {ev.sources && ev.sources.length > 0 && (
                            <div className="mb-2"><SourceLinks sources={ev.sources} /></div>
                          )}
                          <p className="text-xs text-orange-700 bg-orange-50 rounded-lg px-3 py-1.5 mb-3">
                            <Lightbulb className="h-3 w-3 inline mr-1" />
                            {ev.whyItSells}
                          </p>

                          {/* Design Ideas */}
                          {ev.designIdeas && ev.designIdeas.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tasarım Fikirleri</p>
                              <div className="grid gap-2">
                                {ev.designIdeas.map((idea: GundemDesignIdea, iIdx: number) => (
                                  <div key={iIdx} className="rounded-lg bg-gray-50 p-3 border border-gray-100">
                                    <p className="text-xs font-medium text-gray-800 mb-1.5">{idea.idea}</p>
                                    {idea.suggestedText && (
                                      <p className="text-xs text-indigo-600 font-mono bg-indigo-50 rounded px-2 py-1 mb-1.5 inline-block">
                                        "{idea.suggestedText}"
                                      </p>
                                    )}
                                    <div className="flex flex-wrap gap-1.5 mt-1">
                                      {idea.productTypes?.map((pt: string, pIdx: number) => (
                                        <span key={pIdx} className="rounded bg-white border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600">
                                          <ShoppingCart className="h-2.5 w-2.5 inline mr-0.5" />{pt}
                                        </span>
                                      ))}
                                      {idea.targetAudience && (
                                        <span className="rounded bg-blue-50 border border-blue-200 px-1.5 py-0.5 text-[10px] text-blue-600">
                                          <Target className="h-2.5 w-2.5 inline mr-0.5" />{idea.targetAudience}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Add single event to plan */}
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                            <button
                              onClick={() => {
                                const items: PlanItemDraft[] = [
                                  { type: "trend", title: ev.event, content: `${ev.description}\n\nNeden Satar: ${ev.whyItSells}\nKategori: ${ev.category}\nTrend: ${ev.trendWindow}`, enabled: true },
                                  ...(ev.designIdeas || []).map(idea => ({ type: "idea", title: idea.idea, content: `Metin: "${idea.suggestedText}"\nÜrünler: ${idea.productTypes?.join(", ")}\nKitle: ${idea.targetAudience}`, enabled: true })),
                                ];
                                setPlanModal({ title: ev.event, description: ev.description, items, priority: ev.urgency as "high" | "medium" | "low", color: "#f97316" });
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium hover:bg-indigo-100 transition-colors"
                            >
                              <ClipboardList className="h-3.5 w-3.5" />
                              Plana Ekle
                            </button>
                            <AddToPlanButton
                              items={[
                                { type: "trend", title: ev.event, content: `${ev.description}\n\nNeden Satar: ${ev.whyItSells}` },
                                ...(ev.designIdeas || []).map(idea => ({
                                  type: "idea" as string,
                                  title: idea.idea,
                                  content: `Önerilen Metin: "${idea.suggestedText}"\nÜrün Türleri: ${idea.productTypes?.join(", ")}\nHedef Kitle: ${idea.targetAudience}`,
                                })),
                              ]}
                              label="Mevcut Plana Ekle"
                              onCreateNew={() => {
                                const items: PlanItemDraft[] = [
                                  { type: "trend", title: ev.event, content: `${ev.description}\n\nNeden Satar: ${ev.whyItSells}\nKategori: ${ev.category}\nTrend: ${ev.trendWindow}`, enabled: true },
                                  ...(ev.designIdeas || []).map(idea => ({ type: "idea", title: idea.idea, content: `Metin: "${idea.suggestedText}"\nÜrünler: ${idea.productTypes?.join(", ")}\nKitle: ${idea.targetAudience}`, enabled: true })),
                                ];
                                setPlanModal({ title: ev.event, description: ev.description, items, priority: ev.urgency as "high" | "medium" | "low", color: "#f97316" });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Raw content fallback */}
          {gundemM.data && !gundemM.data.gundem && gundemM.data.rawContent && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{gundemM.data.rawContent}</p>
            </div>
          )}
        </div>
      )}

      {/* Plan Modal */}
      {planModal && (
        <CreatePlanModal
          initialTitle={planModal.title}
          initialDescription={planModal.description}
          initialItems={planModal.items}
          initialPriority={planModal.priority}
          planColor={planModal.color}
          onClose={() => setPlanModal(null)}
        />
      )}

      {/* ====================== AI TAB ====================== */}
      {activeTab === "ai" && (
        <div className="space-y-6">
          {/* AI Input */}
          <div className="rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              <h3 className="text-base font-semibold text-indigo-900">AI Trend Analisti</h3>
            </div>
            <p className="text-sm text-indigo-700 mb-3">
              Yaklaşan etkinlikler, takip ettiğiniz mağazalar ve keyword'ler analiz edilerek
              Etsy'de satış potansiyeli yüksek tasarım önerileri üretilir.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={aiContext}
                onChange={(e) => setAiContext(e.target.value)}
                placeholder="Ek bağlam ekleyin (opsiyonel): örneğin 'kupa tasarımlarına odaklan'"
                className="flex-1 rounded-lg border border-indigo-200 px-3 py-2 text-sm bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              />
              <button
                onClick={() => aiM.mutate(aiContext || undefined)}
                disabled={aiM.isPending}
                className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {aiM.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Analiz Et
              </button>
            </div>
            {aiM.isError && (
              <p className="mt-2 text-sm text-red-600">{(aiM.error as Error).message}</p>
            )}
          </div>

          {/* AI Loading */}
          {aiM.isPending && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-10 w-10 animate-spin text-indigo-500 mb-3" />
              <p className="text-sm text-gray-500">AI trend analizi yapılıyor...</p>
              <p className="text-xs text-gray-400 mt-1">Bu işlem 10-30 saniye sürebilir</p>
            </div>
          )}

          {/* AI Results */}
          {aiM.data?.suggestions && (
            <div className="space-y-6">
              {/* Weekly Trends */}
              {aiM.data.suggestions.weeklyTrends && aiM.data.suggestions.weeklyTrends.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
                    <Flame className="h-5 w-5 text-red-500" />
                    Haftalık Trend Önerileri
                  </h3>
                  <div className="grid gap-3">
                    {aiM.data.suggestions.weeklyTrends.map((trend, idx) => (
                      <div key={idx} className="rounded-xl border border-gray-200 bg-white p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-gray-900">{trend.theme}</h4>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{trend.priceRange}</span>
                            <PriorityBadge priority={trend.urgency} />
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{trend.reason}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                          <Eye className="h-3 w-3" />
                          Hedef: {trend.targetAudience}
                        </div>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {trend.keywords.map((kw) => (
                            <span key={kw} className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700">#{kw}</span>
                          ))}
                        </div>
                        <div className="space-y-1">
                          {trend.designIdeas.map((idea, i) => (
                            <p key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
                              <Lightbulb className="h-3 w-3 text-yellow-500 mt-0.5 flex-shrink-0" />
                              {idea}
                            </p>
                          ))}
                        </div>
                        <div className="mt-3 flex justify-end">
                          <AddToPlanButton items={[
                            { type: "trend", title: trend.theme, content: trend.reason, metadata: { urgency: trend.urgency, priceRange: trend.priceRange, targetAudience: trend.targetAudience } },
                            ...trend.keywords.map(kw => ({ type: "keyword" as const, title: kw })),
                            ...trend.designIdeas.map(idea => ({ type: "idea" as const, title: idea, content: `Trend: ${trend.theme}` })),
                          ]} onCreateNew={() => setPlanModal({
                            title: trend.theme,
                            description: trend.reason,
                            priority: trend.urgency === "act_now" ? "high" : "medium",
                            color: "#6366f1",
                            items: [
                              { type: "trend", title: trend.theme, content: `${trend.reason}\nFiyat: ${trend.priceRange}\nKitle: ${trend.targetAudience}`, enabled: true },
                              ...trend.keywords.map(kw => ({ type: "keyword" as string, title: kw, content: `Trend: ${trend.theme}`, enabled: true })),
                              ...trend.designIdeas.map(idea => ({ type: "idea" as string, title: idea, content: `Trend: ${trend.theme}`, enabled: true })),
                            ],
                          })} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly Forecast */}
              {aiM.data.suggestions.monthlyForecast && aiM.data.suggestions.monthlyForecast.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
                    <Calendar className="h-5 w-5 text-blue-500" />
                    Aylık Tahmin
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {aiM.data.suggestions.monthlyForecast.map((month, idx) => (
                      <div key={idx} className="rounded-xl border border-gray-200 bg-white p-4">
                        <h4 className="text-sm font-bold text-gray-900 mb-2">{month.month}</h4>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {month.themes.map((t) => (
                            <span key={t} className="rounded bg-purple-50 px-1.5 py-0.5 text-[10px] text-purple-700">{t}</span>
                          ))}
                        </div>
                        <p className="text-xs text-gray-600 mb-1">
                          <span className="font-medium text-emerald-600">Fırsat:</span> {month.topOpportunity}
                        </p>
                        <p className="text-xs text-gray-500">
                          <span className="font-medium">İpucu:</span> {month.designTip}
                        </p>
                        <div className="mt-2 flex justify-end">
                          <AddToPlanButton items={[
                            { type: "trend", title: `${month.month} - ${month.topOpportunity}`, content: month.designTip, metadata: { themes: month.themes } },
                            ...month.themes.map(t => ({ type: "idea" as const, title: t, content: `${month.month} tahmini` })),
                          ]} onCreateNew={() => setPlanModal({
                            title: `${month.month} Tahmin Planı`,
                            description: month.topOpportunity,
                            items: [
                              { type: "trend", title: month.topOpportunity, content: month.designTip, enabled: true },
                              ...month.themes.map(t => ({ type: "idea", title: t, content: `${month.month} tahmini`, enabled: true })),
                            ],
                            color: "#3b82f6",
                          })} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Niche Opportunities */}
              {aiM.data.suggestions.nicheOpportunities && aiM.data.suggestions.nicheOpportunities.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
                    <Target className="h-5 w-5 text-emerald-500" />
                    Niş Fırsatlar
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {aiM.data.suggestions.nicheOpportunities.map((niche, idx) => (
                      <div key={idx} className="rounded-xl border border-gray-200 bg-white p-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">{niche.niche}</h4>
                        <p className="text-xs text-gray-600 mb-2">{niche.description}</p>
                        <div className="flex items-center gap-2 text-[10px] mb-2">
                          <span className={clsx(
                            "rounded-full px-2 py-0.5 font-medium",
                            niche.competitionLevel === "low" ? "bg-green-100 text-green-700" :
                            niche.competitionLevel === "medium" ? "bg-yellow-100 text-yellow-700" :
                            "bg-red-100 text-red-700"
                          )}>
                            Rekabet: {niche.competitionLevel}
                          </span>
                          <span className={clsx(
                            "rounded-full px-2 py-0.5 font-medium",
                            niche.estimatedDemand === "high" ? "bg-green-100 text-green-700" :
                            niche.estimatedDemand === "medium" ? "bg-yellow-100 text-yellow-700" :
                            "bg-red-100 text-red-700"
                          )}>
                            Talep: {niche.estimatedDemand}
                          </span>
                        </div>
                        <ul className="space-y-0.5">
                          {niche.exampleDesigns.map((d, i) => (
                            <li key={i} className="text-xs text-gray-500 flex items-start gap-1">
                              <Lightbulb className="h-3 w-3 text-yellow-400 mt-0.5 flex-shrink-0" />
                              {d}
                            </li>
                          ))}
                        </ul>
                        <div className="mt-2 flex justify-end">
                          <AddToPlanButton items={[
                            { type: "trend", title: niche.niche, content: niche.description, metadata: { competitionLevel: niche.competitionLevel, estimatedDemand: niche.estimatedDemand } },
                            ...niche.exampleDesigns.map(d => ({ type: "idea" as const, title: d, content: `Niş: ${niche.niche}` })),
                          ]} onCreateNew={() => setPlanModal({
                            title: `Niş: ${niche.niche}`,
                            description: niche.description,
                            items: [
                              { type: "trend", title: niche.niche, content: niche.description, enabled: true },
                              ...niche.exampleDesigns.map(d => ({ type: "idea", title: d, content: `Niş: ${niche.niche}`, enabled: true })),
                            ],
                            color: "#10b981",
                          })} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Items */}
              {aiM.data.suggestions.actionItems && aiM.data.suggestions.actionItems.length > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-emerald-900 flex items-center gap-2">
                      <Zap className="h-5 w-5" />
                      Hemen Yapılacaklar
                    </h3>
                    <AddToPlanButton
                      label="Tümünü Plana Ekle"
                      items={aiM.data.suggestions.actionItems.map((item, idx) => ({
                        type: "note" as const,
                        title: `${idx + 1}. ${item}`,
                      }))}
                      onCreateNew={() => setPlanModal({
                        title: "AI Aksiyon Planı",
                        description: "AI önerilerinden oluşturulan aksiyon planı",
                        items: (aiM.data?.suggestions?.actionItems ?? []).map((item, idx) => ({
                          type: "note", title: `${idx + 1}. ${item}`, content: "", enabled: true,
                        })),
                        color: "#10b981",
                      })}
                    />
                  </div>
                  <ol className="space-y-2">
                    {aiM.data.suggestions.actionItems.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-emerald-800">
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-200 text-xs font-bold text-emerald-700">
                          {idx + 1}
                        </span>
                        {item}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}

          {/* Raw content fallback */}
          {aiM.data && !aiM.data.suggestions && aiM.data.rawContent && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{aiM.data.rawContent}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
